import React, { useCallback, useMemo, useState } from 'react'
import { Fab, type FabProps } from '../../primitives/Fab'
import { type DataTestIdAttribute, type HTMLAttributesWithoutStyling } from '../../types'
import { spacing, useIsVariant } from '../../theme'
import { FabulousMenuContext } from './context'

export type FabulousProps = Pick<FabProps, 'children' | 'variant' | 'href' | 'size' | 'onClick' | 'aria-label'> & {
  /**
   * @default 'normal'
   */
  position?: 'normal' | 'bottomNavigation'

  /**
   * When it needs to be disabled
   */
  isDisabled?: boolean
} & HTMLAttributesWithoutStyling<HTMLButtonElement> &
  DataTestIdAttribute

export const FabulousInternal = ({ children, onClick, position = 'normal', isDisabled, ...props }: FabulousProps) => {
  const isEnabled = useIsVariant(['mobile', 'tablet'])
  const [isOpen, setOpen] = useState(false)
  const [hasMenu, setHasMenu] = useState(false)

  const api = useMemo(
    () => ({
      isOpen,
      hasMenu,
      openMenu: () => {
        setOpen(true)
      },
      closeMenu: () => {
        setOpen(false)
      },
      registerMenu: (arg: boolean) => {
        setHasMenu(arg)
      },
    }),
    [isOpen, hasMenu]
  )

  const handleClick = useCallback<NonNullable<FabProps['onClick']>>(
    (event) => {
      if (hasMenu) {
        event.preventDefault()
        event.stopPropagation()

        if (!isOpen) {
          api.openMenu()
        }
      } else {
        onClick?.(event)
      }
    },
    [hasMenu, isOpen, api, onClick]
  )

  /**
   * We only allow this pattern on mobile or tablet
   */
  if (!isEnabled) {
    return null
  }

  return (
    <FabulousMenuContext.Provider value={api}>
      <Fab
        {...props}
        disabled={isDisabled}
        onClick={handleClick}
        sx={{
          position: 'fixed',
          right: spacing(2),
          bottom: position === 'normal' ? spacing(2) : spacing(8.5),
        }}
      >
        {children}
      </Fab>
    </FabulousMenuContext.Provider>
  )
}
